/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ElbowConnector } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/connectors/elbow-connector';
import { min } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { compact, last, max, uniq } from 'lodash';

interface ConnectorPoints {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

interface Props {
  connectors: ConnectorPoints[];
}

export function ElbowConnectors(props: Props) {
  const connectors = props.connectors;
  const right = connectors.filter(d => d.x1 > d.x2);
  const left = connectors.filter(d => d.x1 <= d.x2);
  return (
    <>
      <ElbowConnectorsSingleDirection connectors={right} />
      <ElbowConnectorsSingleDirection connectors={left} />
    </>
  );
}

function haveVerticalOverlap(a: ConnectorPoints, b: ConnectorPoints) {
  // normalize y coordinates such that y2 is always larger
  const ay1 = Math.min(a.y1, a.y2);
  const ay2 = Math.max(a.y1, a.y2);
  const by1 = Math.min(b.y1, b.y2);
  const by2 = Math.max(b.y1, b.y2);

  return (by2 >= ay1 && ay1 > by1) || (ay2 >= by1 && by1 > ay1);
}

/*
 * Return list of groups of connectors, such that each group
 * contains a set of connector points that have vertical overlap
 */
function overlapGroups(connectors: ConnectorPoints[]) {
  const groups: ConnectorPoints[][] = [];
  groups.push([]);
  for (const connector of connectors) {
    const lastGroup = last(groups)!;
    if (lastGroup.length === 0 || haveVerticalOverlap(last(lastGroup)!, connector)) {
      lastGroup.push(connector);
    } else {
      groups.push([connector]);
    }
  }
  return groups;
}

function ElbowConnectorsSingleDirection(props: Props) {
  const groups = overlapGroups(props.connectors);
  const allElems = compact(
    groups.map((connectors, index) => {
      if (connectors.length === 0) {
        return undefined;
      }
      const rightSide = connectors[0].x1 > connectors[0].x2;

      const xStart = rightSide ? min(connectors.map(d => d.x1))! : max(connectors.map(d => d.x1))!;
      const xEnd = rightSide ? max(connectors.map(d => d.x2))! : min(connectors.map(d => d.x2))!;

      const upwards = connectors[0].y1 > connectors[0].y2;
      const range = upwards ? [xStart, xEnd] : [xEnd, xStart];

      // hacky solution to make sure that connectors starting
      // from the same point branch nicely
      //
      // more proper solutino would be to generalize branching-elbow-connector
      // so that it could be used for everything, including this
      const differents = uniq(connectors.map(c => c.y2)).length;
      const us = scaleLinear()
        .domain([0, differents + 1])
        .range(range);

      let currentIndex = 0;
      let currentY: number | undefined = 0;
      const elems = connectors.map((conn, i) => {
        if (conn.y2 !== currentY) {
          currentIndex++;
          currentY = conn.y2;
        }
        const branchX = us(currentIndex);
        return <ElbowConnector key={i} x1={conn.x1} x2={conn.x2} y1={conn.y1} y2={conn.y2} branchX={branchX} />;
      });
      return <g key={index}>{elems}</g>;
    }),
  );

  return <>{allElems}</>;
}
